// useConfigPoll — the system_config re-read (theme · fields · force_refresh), moved out of
// useAppData's 60 s items poll (AUD-05 P1). It used to run every 60 s, always; now every 5 min
// and only while the tab is visible. Coming back to a visible tab reads at once when the last
// read is 5 min old or more, otherwise it waits out the rest of the 5 min. The items poll in
// useAppData keeps its own 60 s cadence — only this read moved.
//
// The force_refresh baseline: the first ts seen is remembered, a later different ts reloads the
// page (BackupCards' "Refresh signal sent to all users"). useAppData's boot read seeds the
// baseline through the returned ref, so a signal sent before the first poll is not swallowed
// as the baseline.
import { useEffect, useRef } from "react";
import { supabase } from "../supabase";

export const CONFIG_POLL_MS = 5 * 60 * 1000;

export function useConfigPoll({ setTheme, setFields }) {
  const forceRefreshSeenRef = useRef(null);
  const lastReadRef = useRef(Date.now());           // the boot read in useAppData counts as the first

  useEffect(() => {
    let timer = null;
    let alive = true;
    const read = async () => {
      lastReadRef.current = Date.now();
      const { data: configData } = await supabase.from("system_config").select("key,value").in("key", ["theme", "fields", "force_refresh"]);
      if (!alive || !configData) return;
      configData.forEach(row => {
        if (row.key === "theme") setTheme(row.value);
        if (row.key === "fields" && !window.__clb_fields_saving) setFields(row.value);
        if (row.key === "force_refresh") {
          const ts = row.value?.ts;
          if (ts) {
            if (forceRefreshSeenRef.current === null) forceRefreshSeenRef.current = ts;
            else if (ts !== forceRefreshSeenRef.current) { forceRefreshSeenRef.current = ts; window.location.reload(); }
          }
        }
      });
    };
    const schedule = () => {
      clearTimeout(timer);
      const wait = Math.max(0, CONFIG_POLL_MS - (Date.now() - lastReadRef.current));
      timer = setTimeout(async () => {
        timer = null;
        await read();
        if (alive && document.visibilityState === "visible") schedule();
      }, wait);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") schedule();
      else { clearTimeout(timer); timer = null; }
    };
    if (document.visibilityState === "visible") schedule();
    document.addEventListener("visibilitychange", onVisibility);
    return () => { alive = false; clearTimeout(timer); document.removeEventListener("visibilitychange", onVisibility); };
  }, [setTheme, setFields]);   // useState setters — stable, so this runs once

  return forceRefreshSeenRef;
}
